import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import {
  convertMarketplaceAmountToDisplay,
  formatLegalCurrencyAmount,
} from '@core/services/display-currency/convert-display-currency.util';
import { DisplayCurrencyService } from '@core/services/display-currency/display-currency.service';
import type { CurrencyRate } from '@core/services/display-currency/currency-rate.model';
import { formatMarketplacePrice } from '@shared/utils/currency/format-marketplace-price.util';

@Component({
  selector: 'app-price-atom',
  templateUrl: './price-atom.component.html',
  imports: [CommonModule, ContentBuilderColorPipe, TranslateModule],
  providers: [CurrencyPipe],
})
export class PriceAtomComponent {
  @Input() amount!: number;
  @Input() discount = false;
  @Input() format = false;
  @Input() bold = true;
  @Input() semiBold = false;
  @Input() size: 'SM' | 'MD' | 'L' | 'XL' | '2XL' | '3XL' | '4XL' | (string & {}) = '';
  @Input() displayPlus = false;
  @Input() withParenthesis = false;
  @Input() whiteOutline = false;
  @Input() priceColor?: IContentBuilderFieldColor;
  /** Secondary line under an indicative price. `youPay` prefixes the legal amount. */
  @Input() legalCaption: 'amount' | 'youPay' = 'amount';
  /** Tailwind size classes for the legal amount. Smaller than the converted price by default. */
  @Input() legalTextClass = 'text-sm';
  @Input() stackAlign: 'center' | 'end' = 'center';

  readonly displayCurrency = inject(DisplayCurrencyService);

  currency$ = this.marketplaceSelector.selectMarketplaceCurrency;

  constructor(
    private marketplaceSelector: MarketplaceStoreSelector,
    private currencyPipe: CurrencyPipe,
  ) {}

  get priceTextShadow(): string {
    return this.whiteOutline
      ? '1px 1px 2px rgba(255, 255, 255, 0.8), -1px -1px 2px rgba(255, 255, 255, 0.8), 1px -1px 2px rgba(255, 255, 255, 0.8), -1px 1px 2px rgba(255, 255, 255, 0.8)'
      : '';
  }

  /** Font size of the converted price, so the legal line can be 80% of it. */
  get youPayWrapperClasses(): string[] {
    if (this.legalCaption !== 'youPay') {
      return [];
    }
    return this.sizeClassList();
  }

  /** Size classes stay on the price itself, except for "you pay" where the wrapper carries them. */
  get priceLineClasses(): string[] {
    if (this.legalCaption === 'youPay') {
      return [];
    }
    return this.sizeClassList();
  }

  /** "You pay" is white at 80% opacity and 80% of the converted price size. */
  get legalLineClasses(): string {
    if (this.legalCaption === 'youPay') {
      return 'text-[0.8em] text-white opacity-80';
    }
    return `text-neutral-500 ${this.legalTextClass}`;
  }

  private sizeClassList(): string[] {
    const classes = Object.entries(this.priceSizeClasses)
      .filter(([, enabled]) => enabled)
      .map(([className]) => className);
    if (this.size && !classes.includes(this.size)) {
      classes.push(this.size);
    }
    return classes;
  }

  get priceSizeClasses(): Record<string, boolean> {
    if (!this.format) {
      return {
        'text-sm lg:text-base': this.size === 'SM',
        'text-md': this.size === 'MD',
        'text-lg': this.size === 'L',
        'text-xl': this.size === 'XL',
        'text-2xl': this.size === '2XL',
        'text-3xl': this.size === '3XL',
        'text-4xl': this.size === '4XL',
      };
    }
    return {
      'text-sm': this.size === 'SM',
      'text-md': this.size === 'MD',
      'text-lg': this.size === 'L',
      'text-xl': this.size === 'XL',
      'text-2xl': this.size === '2XL',
      'text-3xl': this.size === '3XL',
      'text-4xl': this.size === '4XL',
      'text-base': !this.size,
    };
  }

  formatPrice(amount: number, currency: string, format: boolean): string {
    return formatMarketplacePrice(amount, currency, {
      amountInCents: format,
      currencyPipe: this.currencyPipe,
    });
  }

  formatForeignPrice(amount: number, rate: CurrencyRate): string {
    const converted = convertMarketplaceAmountToDisplay(amount, rate, {
      amountInCents: this.format,
    });
    return formatMarketplacePrice(converted, rate.code, {
      amountInCents: false,
      currencyPipe: this.currencyPipe,
    });
  }

  formatLegalPrice(amount: number, currency: string): string {
    const major = this.format ? amount / 100 : amount;
    return formatLegalCurrencyAmount(major, currency);
  }
}
